/**
 * Admin Prompt Edit Page
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { 
  ArrowLeft, 
  Save, 
  Play, 
  CheckCircle, 
  Archive,
  Clock,
  GitBranch,
  Edit2,
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { 
  usePrompt, 
  useUpdatePrompt,
  useChangePromptStatus,
  usePromptVersions,
  useActivatePromptVersion,
  type PromptStatus,
  type PromptVariable,
} from '@/hooks/use-prompts';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const PROMPT_CATEGORIES = [
  { value: 'rag', label: 'RAG' },
  { value: 'agent', label: 'Agent' },
  { value: 'product_guru', label: 'Product Guru' },
  { value: 'discovery', label: 'Discovery' },
  { value: 'definition', label: 'Definition' },
  { value: 'design', label: 'Design' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'general', label: 'General' },
];

const STATUS_LABELS: Record<PromptStatus, string> = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
  DEPRECATED: 'Deprecated',
};

const STATUS_COLORS: Record<PromptStatus, { bg: string; text: string; border: string }> = {
  DRAFT: { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200' },
  ACTIVE: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  DEPRECATED: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
};

function StatusBadge({ status }: { status: PromptStatus }) {
  const colors = STATUS_COLORS[status];
  const label = STATUS_LABELS[status];
  
  const icons: Record<PromptStatus, React.ReactNode> = {
    ACTIVE: <CheckCircle className="w-3.5 h-3.5" />,
    DRAFT: <Edit2 className="w-3.5 h-3.5" />,
    DEPRECATED: <Archive className="w-3.5 h-3.5" />,
  };

  return (
    <span className={cn(
      'inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border',
      colors.bg,
      colors.text,
      colors.border
    )}>
      {icons[status]}
      {label}
    </span>
  );
}

export default function AdminPromptPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: prompt, isLoading, isError } = usePrompt(id || '');
  const { data: versions } = usePromptVersions(id || '');
  const updatePrompt = useUpdatePrompt();
  const changeStatus = useChangePromptStatus();
  const activateVersion = useActivatePromptVersion(id || '');

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('general');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [userPrompt, setUserPrompt] = useState('');
  const [temperature, setTemperature] = useState<number | undefined>();
  const [maxTokens, setMaxTokens] = useState<number | undefined>();
  const [variables, setVariables] = useState<PromptVariable[]>([]);
  const [changelog, setChangelog] = useState('');

  // UI state
  const [showVersions, setShowVersions] = useState(false);
  const [showVariables, setShowVariables] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [variableModal, setVariableModal] = useState<{ open: boolean; editIndex: number | null }>({ open: false, editIndex: null });
  const [newVariable, setNewVariable] = useState<PromptVariable>({ name: '', description: '', required: false, defaultValue: '' });

  // Populate form when prompt loads
  useEffect(() => {
    if (prompt) {
      setName(prompt.name);
      setDescription(prompt.description || '');
      setCategory(prompt.category);
      setSystemPrompt(prompt.systemPrompt);
      setUserPrompt(prompt.userPrompt || '');
      setTemperature(prompt.temperature ?? undefined);
      setMaxTokens(prompt.maxTokens ?? undefined);
      setVariables((prompt.variables as PromptVariable[]) || []);
    }
  }, [prompt]);

  // Track changes
  useEffect(() => {
    if (!prompt) return;
    
    const changed = 
      name !== prompt.name ||
      description !== (prompt.description || '') ||
      category !== prompt.category ||
      systemPrompt !== prompt.systemPrompt ||
      userPrompt !== (prompt.userPrompt || '') ||
      temperature !== (prompt.temperature ?? undefined) ||
      maxTokens !== (prompt.maxTokens ?? undefined) ||
      JSON.stringify(variables) !== JSON.stringify(prompt.variables || []);
    
    setHasChanges(changed);
  }, [prompt, name, description, category, systemPrompt, userPrompt, temperature, maxTokens, variables]);

  const handleSave = async () => {
    if (!id) return;

    try {
      await updatePrompt.mutateAsync({
        promptId: id,
        name,
        description: description || null,
        category,
        systemPrompt,
        userPrompt: userPrompt || null,
        temperature: temperature ?? null,
        maxTokens: maxTokens ?? null,
        variables: variables.length > 0 ? variables : null,
        changelog: changelog || undefined,
      });
      toast({
        title: 'Prompt Saved',
        description: 'Your changes have been saved successfully',
      });
      setChangelog('');
      setHasChanges(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save prompt',
        variant: 'destructive',
      });
    }
  };

  const handleStatusChange = async (newStatus: PromptStatus) => {
    if (!id) return;

    try {
      await changeStatus.mutateAsync({ promptId: id, status: newStatus });
      toast({
        title: 'Status Updated',
        description: `Prompt is now ${STATUS_LABELS[newStatus]}`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update status',
        variant: 'destructive',
      });
    }
  };

  const handleActivateVersion = async (versionId: string) => {
    try {
      await activateVersion.mutateAsync(versionId);
      toast({
        title: 'Version Activated',
        description: 'The selected version is now active',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to activate version',
        variant: 'destructive',
      });
    }
  };

  const addVariable = () => {
    if (!newVariable.name) return;
    
    if (variableModal.editIndex !== null) {
      const updated = [...variables];
      updated[variableModal.editIndex] = newVariable;
      setVariables(updated);
    } else {
      setVariables([...variables, newVariable]);
    }
    
    setVariableModal({ open: false, editIndex: null });
    setNewVariable({ name: '', description: '', required: false, defaultValue: '' });
  };

  const removeVariable = (index: number) => {
    setVariables(variables.filter((_, i) => i !== index));
  };

  if (isLoading) {
    return (
      <div className="h-screen bg-gray-50 p-8">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-[600px] w-full rounded-lg" />
      </div>
    );
  }

  if (isError || !prompt) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Prompt not found</h2>
          <p className="text-gray-500 mb-4">The prompt you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/admin/prompts')}>
            Back to Prompts
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/admin/prompts"
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Link>
            <div className="h-6 w-px bg-gray-200" />
            <div>
              <h1 className="text-lg font-semibold text-gray-900">{prompt.name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <code className="text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-600">
                  {prompt.key}
                </code>
                <StatusBadge status={prompt.status} />
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <GitBranch className="w-3 h-3" />
                  v{prompt.activeVersion}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {prompt.status === 'DRAFT' && (
              <Button
                variant="outline"
                onClick={() => handleStatusChange('ACTIVE')}
                disabled={changeStatus.isPending}
              >
                <Play className="w-4 h-4 mr-2" />
                Publish
              </Button>
            )}
            {prompt.status === 'ACTIVE' && (
              <Button
                variant="outline"
                onClick={() => handleStatusChange('DEPRECATED')}
                disabled={changeStatus.isPending}
              >
                <Archive className="w-4 h-4 mr-2" />
                Deprecate
              </Button>
            )}
            {prompt.status === 'DEPRECATED' && (
              <Button
                variant="outline"
                onClick={() => handleStatusChange('ACTIVE')}
                disabled={changeStatus.isPending}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Reactivate
              </Button>
            )}
            <Button
              onClick={handleSave}
              disabled={!hasChanges || updatePrompt.isPending}
            >
              <Save className="w-4 h-4 mr-2" />
              {updatePrompt.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex">
        {/* Main Editor */}
        <div className="flex-1 overflow-auto p-6">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Basic Info */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <select
                    id="category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {PROMPT_CATEGORIES.map((cat) => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Brief description of what this prompt does"
                  />
                </div>
              </div>
            </div>

            {/* System Prompt */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">System Prompt</h2>
              <Textarea
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                rows={12}
                className="font-mono text-sm"
                placeholder="You are a helpful AI assistant..."
              />
              <p className="text-xs text-gray-500 mt-2">
                Use <code className="bg-gray-100 px-1 rounded">${'{variableName}'}</code> for dynamic values
              </p>
            </div>

            {/* User Prompt (optional) */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">User Prompt Template (Optional)</h2>
              <Textarea
                value={userPrompt}
                onChange={(e) => setUserPrompt(e.target.value)}
                rows={6}
                className="font-mono text-sm"
                placeholder="Optional template for user messages..."
              />
            </div>

            {/* Variables */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => setShowVariables(!showVariables)}
                  className="flex items-center gap-2 text-lg font-medium text-gray-900"
                >
                  {showVariables ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  Variables ({variables.length})
                </button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setNewVariable({ name: '', description: '', required: false, defaultValue: '' });
                    setVariableModal({ open: true, editIndex: null });
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Variable
                </Button>
              </div>

              {showVariables && (
                <div className="space-y-2">
                  {variables.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No variables defined. Variables allow dynamic content injection.
                    </p>
                  ) : (
                    <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
                      {variables.map((variable, index) => (
                        <div key={index} className="flex items-center justify-between p-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <code className="text-sm font-medium text-indigo-600">${'{' + variable.name + '}'}</code>
                              {variable.required && (
                                <span className="text-xs text-red-500">Required</span>
                              )}
                            </div>
                            {variable.description && (
                              <p className="text-xs text-gray-500 mt-0.5">{variable.description}</p>
                            )}
                            {variable.defaultValue && (
                              <p className="text-xs text-gray-400 mt-0.5">Default: {variable.defaultValue}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => {
                                setNewVariable(variable);
                                setVariableModal({ open: true, editIndex: index });
                              }}
                              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => removeVariable(index)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* LLM Configuration */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">LLM Configuration</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="temperature">Temperature</Label>
                  <Input
                    id="temperature"
                    type="number"
                    step="0.1"
                    min="0"
                    max="2"
                    value={temperature ?? ''}
                    onChange={(e) => setTemperature(e.target.value ? parseFloat(e.target.value) : undefined)}
                    placeholder="0.7"
                  />
                  <p className="text-xs text-gray-500">0 = deterministic, 2 = creative</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="maxTokens">Max Tokens</Label>
                  <Input
                    id="maxTokens"
                    type="number"
                    min="1"
                    value={maxTokens ?? ''}
                    onChange={(e) => setMaxTokens(e.target.value ? parseInt(e.target.value) : undefined)}
                    placeholder="2000"
                  />
                </div>
              </div>
            </div>

            {/* Changelog */}
            {hasChanges && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Change Notes</h2>
                <Input
                  value={changelog}
                  onChange={(e) => setChangelog(e.target.value)}
                  placeholder="Describe what you changed (optional)"
                />
              </div>
            )}
          </div>
        </div>

        {/* Sidebar - Version History */}
        <div className="w-80 border-l border-gray-200 bg-white overflow-auto">
          <div className="p-4 border-b border-gray-200">
            <button
              onClick={() => setShowVersions(!showVersions)}
              className="flex items-center justify-between w-full text-left"
            >
              <h3 className="font-medium text-gray-900">Version History</h3>
              {showVersions ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          </div>

          {showVersions && versions && (
            <div className="divide-y divide-gray-100">
              {versions.map((version) => (
                <div key={version.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">v{version.version}</span>
                      {version.status === 'ACTIVE' && (
                        <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                          Active
                        </span>
                      )}
                    </div>
                    {version.status !== 'ACTIVE' && (
                      <button
                        onClick={() => handleActivateVersion(version.id)}
                        className="text-xs text-indigo-600 hover:text-indigo-700"
                      >
                        Restore
                      </button>
                    )}
                  </div>
                  {version.changelog && (
                    <p className="text-sm text-gray-600 mb-1">{version.changelog}</p>
                  )}
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Clock className="w-3 h-3" />
                    {new Date(version.createdAt).toLocaleDateString()}
                    {version.createdBy?.name && ` by ${version.createdBy.name}`}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Variable Modal */}
      <Dialog open={variableModal.open} onOpenChange={(open) => setVariableModal({ open, editIndex: variableModal.editIndex })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{variableModal.editIndex !== null ? 'Edit Variable' : 'Add Variable'}</DialogTitle>
            <DialogDescription>
              Define a variable that can be dynamically substituted in the prompt.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="varName">Variable Name *</Label>
              <Input
                id="varName"
                placeholder="e.g., projectName"
                value={newVariable.name}
                onChange={(e) => setNewVariable({ ...newVariable, name: e.target.value })}
              />
              <p className="text-xs text-gray-500">Will be used as ${'{variableName}'}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="varDesc">Description</Label>
              <Input
                id="varDesc"
                placeholder="e.g., The name of the project"
                value={newVariable.description}
                onChange={(e) => setNewVariable({ ...newVariable, description: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="varDefault">Default Value</Label>
              <Input
                id="varDefault"
                placeholder="e.g., Untitled Project"
                value={newVariable.defaultValue}
                onChange={(e) => setNewVariable({ ...newVariable, defaultValue: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="varRequired"
                checked={newVariable.required}
                onChange={(e) => setNewVariable({ ...newVariable, required: e.target.checked })}
                className="rounded border-gray-300 text-indigo-600"
              />
              <Label htmlFor="varRequired" className="text-sm">Required</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVariableModal({ open: false, editIndex: null })}>
              Cancel
            </Button>
            <Button onClick={addVariable} disabled={!newVariable.name}>
              {variableModal.editIndex !== null ? 'Update' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
